import { pageClassName } from '@/components/ui/Page'
import { ErrorNotice } from '@/components/ui/Feedback'
import { ArrowLeft, Check, Compass, RefreshCw, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'

import { useRequestTrack, useProviders, useSimilar } from '@/api/hooks'
import type { RelatedArtist, SearchResult } from '@/api/types'
import { toQueueItem, usePlayer } from '@/audio/store'
import { usePendingPlayback } from '@/audio/pendingPlayback'
import { Button, EmptyState, Spinner, toast, TrackListSkeleton } from '@/components/ui'
import {
  AddToPlaylistDialog,
  type AddToPlaylistTarget,
} from '@/features/search/AddToPlaylistDialog'
import { describeError } from '@/features/search/describeError'
import { providerLabel } from '@/features/search/ProviderFilter'
import { SearchResultRow, type ResultAction } from '@/features/search/SearchResultRow'
import { artistSummaryHref } from '@/features/artists/artistHref'

const PAGE = pageClassName('browse')

/** How many neighbouring artists to offer. See the comment where it is used. */
const NEIGHBOUR_LIMIT = 10

/** Identity of a row, matching how search keys its results. */
function keyOf(result: SearchResult): string {
  return `${result.provider}:${result.external_id}`
}

/**
 * "More like this" — the way into music you did not already know to look for.
 *
 * Until this existed the only entrance to the app was a search box, which meant
 * you had to arrive holding the answer. This starts from something you already
 * like and shows you its neighbours.
 *
 * Suggestions load metadata only. Playing a song requests a temporary copy;
 * adding it to the library keeps that copy permanently.
 */
export function SimilarPage() {
  // Two ways to arrive, because the callers genuinely differ. From your own
  // library you have the catalog's artist id; from a neighbour chip you only
  // have the provider's, and there may be no catalog row for them at all.
  const { artistId, localArtistId, provider, externalId } = useParams()
  const [params] = useSearchParams()
  const seedId = Number(artistId)

  const similar = useSimilar(
    localArtistId ? { localArtistId } : provider && externalId
      ? { provider, artist: externalId }
      : Number.isFinite(seedId) && seedId > 0
        ? { artistId: seedId }
        : null,
  )
  const { data: providers = [] } = useProviders()
  const addTrack = useRequestTrack()

  const playQueue = usePlayer((state) => state.playQueue)
  const playNextUp = usePlayer((state) => state.playNextUp)
  const enqueue = usePlayer((state) => state.enqueue)

  const [busy, setBusy] = useState<Record<string, boolean>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [playlistTarget, setPlaylistTarget] = useState<AddToPlaylistTarget | null>(null)

  const seedName = similar.data?.seed.name ?? params.get('name') ?? 'this'
  const tracks = similar.data?.tracks ?? []
  // Ten is plenty for a sideways hop, and the row stays about two screens wide
  // rather than five. The provider returns up to twenty, ranked, so the tail is
  // the least relevant part of it anyway.
  const neighbours = (similar.data?.artists ?? []).slice(0, NEIGHBOUR_LIMIT)

  async function act(result: SearchResult, action: ResultAction) {
    const key = keyOf(result)
    setBusy((b) => ({ ...b, [key]: true }))
    setErrors(({ [key]: _gone, ...rest }) => rest)
    try {
      const response = await addTrack.mutateAsync({
        intent: action === 'download' ? 'save' : 'play',
        provider: result.provider,
        external_id: result.external_id,
      })
      const item = toQueueItem(response.library_item)

      if (!item) {
        // Still downloading. Remember the intent so it starts on its own.
        if (action !== 'download') {
          usePendingPlayback
            .getState()
            .requestWhenReady(response.library_item.id, action, 'search')
        }
        toast.info(
          `Downloading ${result.title}…`,
          action === 'download'
            ? 'Follow it from Downloads.'
            : 'It will start playing when it is ready.',
        )
        return
      }

      switch (action) {
        case 'play':
          playQueue([item], 0, { source: 'search' })
          break
        case 'next':
          playNextUp([item])
          toast.success('Playing next', result.title)
          break
        case 'queue':
          enqueue([item])
          toast.success('Added to queue', result.title)
          break
        case 'download':
          toast.success('Added to library', result.title)
          break
      }
    } catch (thrown) {
      setErrors((e) => ({ ...e, [key]: describeError(thrown, 'Could not add that track.') }))
    } finally {
      setBusy((b) => ({ ...b, [key]: false }))
    }
  }

  return (
    <div className={PAGE}>
      <button
        type="button"
        onClick={() => window.history.back()}
        className="-ml-2 mb-3 inline-flex h-11 items-center gap-1.5 rounded-lg px-2 text-sm text-text-muted transition-colors hover:bg-surface-hover hover:text-text"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Back
      </button>

      <header className="mb-5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-xs font-medium tracking-wide text-accent uppercase">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            More like
          </p>
          <h1 className="mt-1 truncate text-2xl font-semibold tracking-tight text-text">
            {seedName}
          </h1>
          {tracks.length > 0 ? (
            <p className="mt-1 text-sm text-text-muted">
              Play any song to try it. Add it to your library to keep it permanently.
            </p>
          ) : null}
        </div>
        {tracks.length > 0 ? (
          <Button
            variant="secondary"
            size="sm"
            className="shrink-0"
            loading={similar.isFetching}
            onClick={() => void similar.refetch()}
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Shuffle
          </Button>
        ) : null}
      </header>

      {/* The sideways hop. Each of these re-seeds the page, so one liked song can
          be walked outwards indefinitely. */}
      {neighbours.length > 0 ? (
        <nav
          aria-label="Related artists"
          className="scrollbar-none -mx-4 mb-5 overflow-x-auto px-4"
        >
          {/* `w-max` plus `shrink-0` on each item, because the <li> is the flex
              child: putting shrink-0 on the link inside it let every pill
              compress and clip its own name. */}
          <ul className="flex w-max gap-2">
            {neighbours.map((artist: RelatedArtist) => (
              <li key={`${artist.provider}:${artist.external_id}`} className="shrink-0">
                <Link
                  to={artistSummaryHref(artist)}
                  className="flex h-8 items-center gap-1.5 rounded-full border border-border bg-surface-raised pr-2.5 pl-1 text-sm whitespace-nowrap text-text transition-colors hover:bg-surface-hover"
                >
                  {artist.picture_url ? (
                    <img
                      src={artist.picture_url}
                      alt=""
                      loading="lazy"
                      className="h-6 w-6 rounded-full object-cover"
                    />
                  ) : (
                    <span className="h-6 w-6 rounded-full bg-surface-hover" aria-hidden="true" />
                  )}
                  {/* Capped, because one pathological name — Deezer really does
                      return "The Royal Concertgebouw Orchestra of Amsterdam feat.
                      Martha Argerich" — otherwise makes a single pill wider than
                      the phone it is on. */}
                  <span className="max-w-36 truncate">{artist.name}</span>
                  {artist.in_library ? (
                    <Check
                      className="h-3.5 w-3.5 shrink-0 text-text-subtle"
                      aria-label="in your library"
                    />
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}

      {similar.isPending ? (
        <TrackListSkeleton count={8} />
      ) : similar.isError ? (
        <ErrorNotice title="Could not find anything similar" busy={similar.isFetching} onRetry={() => void similar.refetch()}>
          {describeError(similar.error, 'The music source did not answer.')}
        </ErrorNotice>
      ) : tracks.length === 0 ? (
        <EmptyState
          icon={Compass}
          title="Nothing to suggest"
          description="This artist has no radio on this music source. Try one of their neighbours, or a different song."
        />
      ) : (
        <ul className="flex flex-col">
          {tracks.map((result) => {
            const key = keyOf(result)
            return (
              <li key={key}>
                <SearchResultRow
                  result={result}
                  status={result.library_status ?? undefined}
                  busy={busy[key] === true}
                  error={
                    errors[key]
                      ? { message: errors[key], retry: () => void act(result, 'download') }
                      : undefined
                  }
                  showProvider={false}
                  providerName={providerLabel(providers, result.provider)}
                  onAction={(action) => void act(result, action)}
                  onAddToPlaylist={() =>
                    setPlaylistTarget({
                      title: result.title,
                      provider: result.provider,
                      externalId: result.external_id,
                      trackId: result.track_id ?? null,
                    })
                  }
                  onDismissError={() =>
                    setErrors(({ [key]: _gone, ...rest }) => rest)
                  }
                />
              </li>
            )
          })}
        </ul>
      )}

      {playlistTarget ? (
        <AddToPlaylistDialog
          key={`${playlistTarget.provider}:${playlistTarget.externalId}`}
          target={playlistTarget}
          onOpenChange={(open) => {
            if (!open) setPlaylistTarget(null)
          }}
        />
      ) : null}

      {similar.isFetching && !similar.isPending ? (
        <div className="flex justify-center py-4">
          <Spinner size="sm" className="text-text-subtle" label="Finding more" />
        </div>
      ) : null}
    </div>
  )
}
